import { useFormik } from 'formik';
import { Dispatch, FC, SetStateAction, useMemo, useState } from 'react';
import styled from 'styled-components';
import { colors } from '../../../../component/colors';
import { DropDownWithLabel } from '../../../../component/dropDown';
import { SearchableDropDownWithLabel } from '../../../../component/dropDown/searchableDropDownWthLabel';
import { ImagePicker } from '../../../../component/imagePicker';
import { Input } from '../../../../component/input';
import { AddModal } from '../../../../component/modal';
import { TextAreaWithLabel } from '../../../../component/textArea';
import {
  useEditPromotionsMutation,
  useFetchVendorsQuery
} from '../../../../store/promotion/api';
import { IPromotion } from '../../../../store/promotion/interface';
import { pxToRem } from '../../../../utils/pxToRem';
import { showErrorToast, showSuccessToast } from '../../../../utils/toast';
import { generateUrl } from '../../../../utils/urlGenerator';
import useDebounce from '../../../../utils/useDebounce';
import { editPromotionValidation } from '../../validation';
type IEditPromotions = {
  setShow: Dispatch<SetStateAction<boolean>>;
  promotionInfo: IPromotion;
};
type FormikValues = {
  category: string;
  duration: number;
  vendor: { name: string; index: number };
  description: string;
  file: File[] | null;
};
export const EditPromotion: FC<IEditPromotions> = ({
  setShow,
  promotionInfo
}) => {
  const [searchVendor, setSearchVendor] = useState('');
  const debounceSearchVendor = useDebounce(searchVendor, 500);
  const [editPromotions, { isLoading: editPromotionsLoading }] =
    useEditPromotionsMutation();

  const timeFrame = useMemo(() => {
    const dateDifference =
      new Date(promotionInfo.duration.to).getTime() -
      new Date(promotionInfo.duration.from).getTime();
    return dateDifference / (7 * 24 * 60 * 60 * 1000);
  }, [promotionInfo.duration]);

  const handleEditPromotions = (values: FormikValues) => {
    if (!vendors?.data) return;
    const formData = new FormData();
    formData.append(
      'category',
      values.category.replace(' ', '-').toLowerCase()
    );
    formData.append('weekNo', values.duration.toString());
    formData.append(
      'vendor',
      vendors?.data.fetchedData[values.vendor.index].id ?? ''
    );
    if (values.description !== promotionInfo.description) {
      formData.append('description', values.description);
    }

    if (values.file) {
      values.file.forEach((file) => {
        formData.append('file', file);
      });
    }
    editPromotions({ body: formData, id: promotionInfo._id })
      .unwrap()
      .then((result) => {
        showSuccessToast(result.message);
        setShow(false);
      })
      .catch((error) => {
        showErrorToast(error.message);
      });
  };

  const { values, handleChange, handleSubmit, setFieldValue } = useFormik({
    initialValues: {
      category: promotionInfo.category,
      duration: timeFrame,
      vendor: { name: promotionInfo.vendorName, index: -1 },
      description: promotionInfo.description,
      file: null
    },
    validationSchema: editPromotionValidation,
    onSubmit: handleEditPromotions
  });

  const handleFilePicked = (file: File[]) => {
    setFieldValue('file', file);
  };
  const { data: vendors } = useFetchVendorsQuery(
    generateUrl({
      specific_module: values?.category?.toLowerCase().replace(' ', '-') ?? '',
      search: debounceSearchVendor
    })
  );

  const VendorList = useMemo(() => {
    if (values.category === '') return [];
    return (
      vendors?.data.fetchedData.map((vendor) => ({
        name: vendor.name,
        imageUrl: vendor.file[0].url
      })) ?? []
    );
  }, [vendors, values.category]);

  return (
    <AddModal
      title="Edit Promotion"
      disableBtn={editPromotionsLoading}
      btnTxt={'Edit Promotion'}
      btnClick={handleSubmit}
      closeModal={() => setShow(false)}
      isLoading={editPromotionsLoading}
    >
      <Container>
        <Row>
          <CustomDropDown
            label="Category"
            value={values.category}
            onSelect={(item) => {
              setFieldValue('category', item);
            }}
            options={['Real estate', 'Shopping', 'Hotel', 'Lifestyle']}
          />

          <CustomInput
            labelText={'Duration (Number of weeks)'}
            value={values.duration}
            type={'number'}
            min={1}
            onChange={handleChange('duration')}
          />
        </Row>
        <Row>
          <SearchableCustomDropDown
            search={searchVendor}
            setSearch={setSearchVendor}
            label="Vendor"
            value={values.vendor.name}
            onSelect={(item, index) => {
              setFieldValue('vendor', { name: item, index });
            }}
            options={VendorList}
          />
        </Row>
        <CustomTextAreaWithLabel
          labelText="Description"
          placeholder="Enter description"
          value={values.description}
          onChange={handleChange('description')}
        />
        <Label>Promotion image(s)</Label>
        <CustomImagePicker
          initialValue={promotionInfo?.files.map((image) => image.url)}
          onFilePicked={handleFilePicked}
        />
      </Container>
    </AddModal>
  );
};
const Container = styled.div`
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 0px 40px;
  overflow-y: auto;
`;
const Row = styled.div`
  display: flex;
  justify-content: space-between;
  @media screen and (max-width: 600px) {
    flex-direction: column;
  }
`;
const CustomInput = styled(Input)`
  flex: 0.46;
  color: ${colors.textDark};
  margin-bottom: 16px;
`;
const CustomImagePicker = styled(ImagePicker)`
  margin-bottom: 32px;
`;
const CustomTextAreaWithLabel = styled(TextAreaWithLabel)`
  margin-bottom: 16px;
`;
const CustomDropDown = styled(DropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
const Label = styled.p`
  color: ${colors.textDark};
  font-weight: 500;
  font-size: ${pxToRem(16)};
  line-height: ${pxToRem(20)};
  text-align: left;
  margin-bottom: 8px;
`;
const SearchableCustomDropDown = styled(SearchableDropDownWithLabel)`
  flex: 0.46;
  margin-bottom: 16px;
`;
